import React, { useState, useEffect, useRef } from 'react';
import { useDS5Controller } from '../hooks/useDS5Controller';
import { useMovementCache } from '../hooks/useMovementCache';
import { useDS5Status } from '../hooks/useDS5Status';
import { api } from '../services/api';
import './PackagingStation.css';
import packageJson from '../../package.json';
import Cadance from './Cadance';
const APP_VERSION = packageJson.version;

const PackagingStation = ({
  userInfo,
  userPosition,
  stationName = "Packaging",
  nextJobOnA = "Quality Control",
  nextJobOnB = "Quality Control",
  nextJobOnC = "Quality Control",
}) => {
  const { serialNumber, isConnected, ds5color } = useDS5Controller();

  const {
    ds5Status,
    updateDS5Status,
    resetDS5Status
  } = useDS5Status();

  const movementsCache = useMovementCache();

  const [ds5StatusAtSave, setDs5StatusAtSave] = useState('');
  const [warning, setWarning] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [nextJob, setNextJob] = useState('');
  const [hourcount, setHourCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [currentMovementData, setCurrentMovementData] = useState(null);
  const [hasBeenProcessed, setHasBeenProcessed] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [autoSaveCompleted, setAutoSaveCompleted] = useState(false);
  const [shouldCheckStatus, setShouldCheckStatus] = useState(false);
  const [statusCheckCompleted, setStatusCheckCompleted] = useState(false);
  const [isFailSaving, setIsFailSaving] = useState(false);

  // Palette management states
  const [palettes, setPalettes] = useState([]);
  const [selectedPaletteId, setSelectedPaletteId] = useState('');
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [isCreatingPalette, setIsCreatingPalette] = useState(false);
  const [showPaletteWarning, setShowPaletteWarning] = useState(false);
  const [paletteWarningMessage, setPaletteWarningMessage] = useState('');

  // Palette creation confirmation states
  const [showPaletteConfirmation, setShowPaletteConfirmation] = useState(false);
  const [pendingPaletteInfo, setPendingPaletteInfo] = useState(null);

  // Palette switch confirmation states
  const [showPaletteSwitchConfirmation, setShowPaletteSwitchConfirmation] = useState(false);
  const [paletteSwitchInfo, setPaletteSwitchInfo] = useState(null);

  // Panne / Fail modal states
  const [showFailModal, setShowFailModal] = useState(false);
  const [pannes, setPannes] = useState([]);
  const [selectedPanneId, setSelectedPanneId] = useState('');
  const [selectedPanneInfo, setSelectedPanneInfo] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [availableMovements, setAvailableMovements] = useState([]);
  const [selectedMovementId, setSelectedMovementId] = useState('');
  const [modalError, setModalError] = useState('');

  const isCheckingRef = useRef(false);
  const prevSerialNumberRef = useRef('');
  const hasStatusBeenCheckedRef = useRef(false);
  const statusInitialLoadRef = useRef(true);
  // Tracks the id_mvtpal of the selected palette to detect DB status changes
  const selectedPaletteMvtRef = useRef(null);

  // ─── Derived: is the VALIDATE button truly ready? ───────────────────────────
  const canValidate =
    !!selectedGrade &&
    !!selectedPaletteId &&
    !showPaletteWarning &&
    !isCreatingPalette &&
    !showPaletteConfirmation &&
    !showPaletteSwitchConfirmation &&
    !isSaving;

  const getMovementNameById = (movementId) => {
    if (!movementsCache || Object.keys(movementsCache).length === 0) return null;
    for (const [name, id] of Object.entries(movementsCache)) {
      if (id === movementId) return name;
    }
    return null;
  };

  const getNextJobByStatusAndGrade = (grade) => {
    if (ds5Status.status === 'ko') return "Repair";
    if (ds5Status.status === 'unknown') return "Full Test";
    const nextJobMap = { A: nextJobOnA, B: nextJobOnB, C: nextJobOnC };
    return nextJobMap[grade] || nextJobOnA;
  };

  const getColorCategory = (color) => {
    if (!color) return 'Multicolor';
    const lowerColor = color.toLowerCase();
    if (lowerColor === 'white') return 'White';
    if (lowerColor === 'black') return 'Black';
    return 'Multicolor';
  };

  // ─── Sync selectedPalette object + detect id_mvtpal change in DB ─────────────
  // The ONLY place selectedPaletteId is auto-cleared — when the palette's
  // movement status changes in the DB (detected on periodic refresh).
  useEffect(() => {
    if (!selectedPaletteId) {
      setSelectedPalette(null);
      selectedPaletteMvtRef.current = null;
      return;
    }
    const palette = palettes.find(p => p.id === Number(selectedPaletteId));
    if (!palette) {
      setSelectedPalette(null);
      selectedPaletteMvtRef.current = null;
      return;
    }
    // If id_mvtpal changed since we last set it → palette moved to another status
    if (
      selectedPaletteMvtRef.current !== null &&
      selectedPaletteMvtRef.current !== palette.id_mvtpal
    ) {
      console.log('Palette mvt changed in DB, clearing selection');
      setSelectedPaletteId('');
      setSelectedPalette(null);
      selectedPaletteMvtRef.current = null;
      setWarning('⚠️ The selected palette status changed. Please select a new palette.');
      return;
    }
    setSelectedPalette(palette);
    selectedPaletteMvtRef.current = palette.id_mvtpal;
  }, [selectedPaletteId, palettes]);

  // ─── Load palettes on mount ──────────────────────────────────────────────────
  useEffect(() => {
    loadPalettes();
  }, []);

  // ─── Periodic palette refresh (detects mvt changes in DB) ───────────────────
  useEffect(() => {
    const intervalId = setInterval(() => {
      loadPalettes();
    }, 10000);
    return () => clearInterval(intervalId);
  }, []);

  const loadPalettes = async () => {
    try {
      const palettesData = await api.getPalette(1);
      if (Array.isArray(palettesData)) {
        setPalettes(palettesData);
        return palettesData;
      }
      return [];
    } catch (err) {
      console.error('Error loading palettes:', err);
      return [];
    }
  };

  // ─── Load pannes & movements when fail modal opens ───────────────────────────
  useEffect(() => {
    if (!showFailModal) return;
    Promise.all([
      api.getAllDs5Panne(),
      api.getAllMovements ? api.getAllMovements() : Promise.resolve([])
    ])
      .then(([pannesData, movementsData]) => {
        const excludedPannes = ['Rayure légere', 'Grattage léger', 'Rayure profonde', 'Grattage profond'];
        const filteredPannes = Array.isArray(pannesData)
          ? pannesData.filter(p => !excludedPannes.includes(p.panne || p.name))
          : [];
        setPannes(filteredPannes);
        if (Array.isArray(movementsData) && movementsData.length > 0) {
          setAvailableMovements(movementsData);
        } else {
          const movementsFromCache = Object.entries(movementsCache || {}).map(([name, id]) => ({ id, name }));
          setAvailableMovements(movementsFromCache);
        }
      })
      .catch(err => {
        setPannes([]);
        setAvailableMovements([]);
        setWarning('Erreur chargement des données : ' + err.message);
      });
  }, [showFailModal, movementsCache]);

  const handlePanneSelection = (panneId) => {
    const panne = pannes.find(p => p.id === Number(panneId));
    setSelectedPanneInfo(panne || null);
  };

  const prepareConfirmation = () => {
    setModalError('');
    if (!selectedPanneId) {
      setModalError("Veuillez sélectionner un type de panne");
      return;
    }
    const data = {
      panneText: selectedPanneInfo?.panne || selectedPanneInfo?.name || '',
      grade: selectedPanneInfo?.grade || null,
      target: "Repair"
    };
    setConfirmationData(data);
    setShowFailModal(false);
    setTimeout(() => setShowConfirmationModal(true), 50);
  };

  const handleFailConfirm = async () => {
    if (!confirmationData) return;
    if (!userInfo || !userInfo.id) {
      setWarning("Aucun opérateur connecté. Veuillez vous authentifier.");
      setShowConfirmationModal(false);
      return;
    }
    if (!currentMovementData || !movementsCache || Object.keys(movementsCache).length === 0) {
      setWarning("Données manquantes pour l'enregistrement");
      setShowConfirmationModal(false);
      return;
    }
    const movementId = movementsCache[stationName];
    const nextJobId = movementsCache[confirmationData.target];
    if (!movementId || !nextJobId) {
      setWarning("Erreur: IDs des mouvements non trouvés");
      setShowConfirmationModal(false);
      return;
    }
    const payload = {
      serial_number: serialNumber,
      operator: userInfo.id,
      tester: stationName,
      movement: movementId,
      nextjob: nextJobId,
      Grade: confirmationData.grade || '',
      panne: selectedPanneId ? Number(selectedPanneId) : null,
      version: APP_VERSION,
      color: ds5color,
      id_palette: null
    };
    try {
      setIsFailSaving(true);
      setDs5StatusAtSave(ds5Status.status);
      await api.saveMovement(payload);
      setHourCount(prev => prev + 1);
      setNextJob(confirmationData.target);
      setSelectedGrade('');
      setShowSuccess(true);
      setHasBeenProcessed(true);
      setShowPaletteWarning(false);
      // ✅ Palette stays selected
    } catch (err) {
      console.error("Erreur sauvegarde FAIL:", err);
      setWarning(`Erreur enregistrement FAIL: ${err.message}`);
    } finally {
      setIsFailSaving(false);
      setShowConfirmationModal(false);
      setSelectedPanneId('');
      setSelectedPanneInfo(null);
      setSelectedMovementId('');
      setConfirmationData(null);
      setModalError('');
    }
  };

  // ─── Palette creation ────────────────────────────────────────────────────────
  const confirmPaletteCreation = async () => {
    if (!pendingPaletteInfo) return;
    const { grade, colorCategory } = pendingPaletteInfo;
    try {
      setIsCreatingPalette(true);
      setShowPaletteConfirmation(false);
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = String(now.getFullYear()).slice(-2);
      const baseSN = `DS5${day}${month}${year}`;
      const response = await api.getPalette();
      const existingPalettes = Array.isArray(response) ? response : [];
      const todayPalettes = existingPalettes
        .filter(p => p.sn_palette && p.sn_palette.startsWith(baseSN))
        .map(p => {
          const counter = parseInt(p.sn_palette.slice(-4), 10);
          return isNaN(counter) ? 0 : counter;
        });
      const maxCounter = todayPalettes.length > 0 ? Math.max(...todayPalettes) : 0;
      const nextCounter = maxCounter + 1;
      const paletteSn = `${baseSN}${String(nextCounter).padStart(4, '0')}`;
      const id_mvtpal = 1;
      await api.createPalette(paletteSn, grade, colorCategory, id_mvtpal);
      const updatedPalettes = await api.getPalette();
      setPalettes(updatedPalettes);
      const newPalette = updatedPalettes.find(p => p.sn_palette === paletteSn);
      if (newPalette) {
        selectedPaletteMvtRef.current = newPalette.id_mvtpal;
        setSelectedPaletteId(String(newPalette.id));
        setSelectedPalette(newPalette);
        setShowPaletteWarning(false);
        setPaletteWarningMessage('');
      }
    } catch (err) {
      console.error('Error creating palette:', err);
      setWarning('Erreur lors de la création automatique de la palette');
      setSelectedGrade('');
    } finally {
      setIsCreatingPalette(false);
      setPendingPaletteInfo(null);
    }
  };

  const cancelPaletteCreation = () => {
    setShowPaletteConfirmation(false);
    setPendingPaletteInfo(null);
    setSelectedGrade('');
  };

  const createNewPalette = (grade, colorCategory) => {
    if (isCreatingPalette) return;
    setPendingPaletteInfo({ grade, colorCategory });
    setShowPaletteConfirmation(true);
  };

  // ─── Palette switch ──────────────────────────────────────────────────────────
  const confirmPaletteSwitch = () => {
    if (!paletteSwitchInfo) return;
    const { newPalette } = paletteSwitchInfo;
    selectedPaletteMvtRef.current = newPalette.id_mvtpal;
    setSelectedPaletteId(String(newPalette.id));
    setSelectedPalette(newPalette);
    setShowPaletteWarning(false);
    setPaletteWarningMessage('');
    setShowPaletteSwitchConfirmation(false);
    setPaletteSwitchInfo(null);
  };

  const cancelPaletteSwitch = () => {
    setShowPaletteSwitchConfirmation(false);
    setPaletteSwitchInfo(null);
    setSelectedGrade('');
  };

  // ─── When grade changes, check palette compatibility ─────────────────────────
  // Case 1: current palette compatible + not full → keep it, nothing happens
  // Case 2: current palette incompatible/full + compatible exists → offer switch
  // Case 3: no compatible palette at all → offer to create
  // Never auto-selects — operator always chooses manually from dropdown
  useEffect(() => {
    if (!selectedGrade || !ds5color) return;

    const colorCategory = getColorCategory(ds5color);

    loadPalettes().then((freshPalettes) => {
      const currentPalette = freshPalettes.find(p => p.id === Number(selectedPaletteId));

      // Case 1: compatible and not full → nothing to do
      if (
        currentPalette &&
        currentPalette.grad === selectedGrade &&
        currentPalette.color === colorCategory &&
        currentPalette.qty < currentPalette.capacity
      ) {
        setShowPaletteWarning(false);
        setPaletteWarningMessage('');
        return;
      }

      const matchingPalette = freshPalettes.find(p =>
        p.grad === selectedGrade &&
        p.color === colorCategory &&
        p.capacity > 0 &&
        p.qty < p.capacity
      );

      // Case 2: have a palette but it's wrong + a compatible one exists → switch
      if (currentPalette && matchingPalette) {
        setPaletteSwitchInfo({ oldPalette: currentPalette, newPalette: matchingPalette, grade: selectedGrade, colorCategory });
        setShowPaletteSwitchConfirmation(true);
        return;
      }

      // Case 3: no compatible palette at all → create
      if (!matchingPalette) {
        createNewPalette(selectedGrade, colorCategory);
      }
    });
  }, [selectedGrade]);

  // ─── Validate palette compatibility on selection/grade/color/palettes change ──
  useEffect(() => {
    if (!selectedGrade || !ds5color || !selectedPaletteId) {
      setShowPaletteWarning(false);
      return;
    }
    const palette = palettes.find(p => p.id === Number(selectedPaletteId));
    if (!palette) return;

    const colorCategory = getColorCategory(ds5color);
    const warnings = [];
    if (palette.grad !== selectedGrade) warnings.push(`Grade mismatch (Palette: ${palette.grad}, Controller: ${selectedGrade})`);
    if (palette.color !== colorCategory) warnings.push(`Color mismatch (Palette: ${palette.color}, Controller: ${colorCategory})`);
    if (palette.qty >= palette.capacity) warnings.push(`Palette is full (${palette.qty}/${palette.capacity})`);

    if (warnings.length > 0) {
      setPaletteWarningMessage('⚠️ ' + warnings.join(' — '));
      setShowPaletteWarning(true);
    } else {
      setShowPaletteWarning(false);
      setPaletteWarningMessage('');
    }
  }, [selectedGrade, selectedPaletteId, ds5color, palettes]);

  // ─── Hourly count reset ──────────────────────────────────────────────────────
  useEffect(() => {
    const resetHourlyCount = () => setHourCount(0);
    const now = new Date();
    const msUntilNextHour =
      (60 - now.getMinutes()) * 60 * 1000 -
      now.getSeconds() * 1000 -
      now.getMilliseconds();
    const timeoutId = setTimeout(() => {
      resetHourlyCount();
      const intervalId = setInterval(resetHourlyCount, 60 * 60 * 1000);
      return () => clearInterval(intervalId);
    }, msUntilNextHour);
    return () => clearTimeout(timeoutId);
  }, []);

  // ─── Connection / movement history check ─────────────────────────────────────
  useEffect(() => {
    if (!isConnected || !serialNumber || serialNumber.includes('Error')) {
      setWarning('');
      setCurrentMovementData(null);
      setHasBeenProcessed(false);
      setSelectedGrade('');
      setAutoSaveCompleted(false);
      setShouldCheckStatus(false);
      setStatusCheckCompleted(false);
      setShowPaletteWarning(false);
      setShowPaletteConfirmation(false);
      setPendingPaletteInfo(null);
      setShowPaletteSwitchConfirmation(false);
      setPaletteSwitchInfo(null);
      // ✅ Palette stays selected on disconnect
      resetDS5Status();
      hasStatusBeenCheckedRef.current = false;
      statusInitialLoadRef.current = true;
      return;
    }

    if (!movementsCache || Object.keys(movementsCache).length === 0) return;

    const checkMovementHistory = async () => {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;
      try {
        const data = await api.getCurrentMovement(serialNumber);
        if (!data) {
          setCurrentMovementData(null);
          setWarning("Controller not in database - Start with Quick Drift");
          setHasBeenProcessed(false);
          setShouldCheckStatus(false);
          setStatusCheckCompleted(false);
          resetDS5Status();
          hasStatusBeenCheckedRef.current = false;
          statusInitialLoadRef.current = true;
          return;
        }
        const movementName = getMovementNameById(data.id_movement);
        const nextJobName = getMovementNameById(data.id_nextjob);
        const processedData = {
          ...data,
          movement: movementName,
          nextjob: nextJobName,
          id_movement: data.id_movement,
          id_nextjob: data.id_nextjob
        };
        setCurrentMovementData(processedData);
        if (processedData.nextjob !== stationName) {
          setHasBeenProcessed(true);
          setWarning('');
          setShouldCheckStatus(false);
          setStatusCheckCompleted(false);
          resetDS5Status();
          hasStatusBeenCheckedRef.current = false;
          statusInitialLoadRef.current = true;
          return;
        }
        setWarning('');
        setHasBeenProcessed(false);
        setShouldCheckStatus(true);
        setStatusCheckCompleted(false);
        hasStatusBeenCheckedRef.current = false;
        statusInitialLoadRef.current = true;
      } catch (err) {
        console.error("Erreur vérification historique:", err);
        if (err.response?.status === 404 || err.message.includes('404')) {
          setCurrentMovementData(null);
          setWarning("Controller not in database - Start with Quick Drift");
          setHasBeenProcessed(false);
          setShouldCheckStatus(false);
          setStatusCheckCompleted(false);
          resetDS5Status();
          hasStatusBeenCheckedRef.current = false;
          statusInitialLoadRef.current = true;
        } else {
          setWarning("Erreur de vérification de l'historique");
          setHasBeenProcessed(false);
          setShouldCheckStatus(false);
          setStatusCheckCompleted(false);
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkMovementHistory();
  }, [isConnected, serialNumber, movementsCache, stationName, resetDS5Status]);

  // ─── DS5 status check ────────────────────────────────────────────────────────
  useEffect(() => {
    const serialChanged = serialNumber !== prevSerialNumberRef.current;
    if (
      isConnected &&
      serialNumber &&
      !serialNumber.includes('Error') &&
      serialChanged &&
      shouldCheckStatus &&
      currentMovementData &&
      currentMovementData.nextjob === stationName &&
      !hasStatusBeenCheckedRef.current
    ) {
      prevSerialNumberRef.current = serialNumber;
      hasStatusBeenCheckedRef.current = true;
      statusInitialLoadRef.current = true;
      updateDS5Status(serialNumber);
    } else if ((!isConnected || !serialNumber || serialNumber.includes('Error')) && prevSerialNumberRef.current) {
      prevSerialNumberRef.current = '';
      resetDS5Status();
      hasStatusBeenCheckedRef.current = false;
      statusInitialLoadRef.current = true;
      setStatusCheckCompleted(false);
    }
  }, [isConnected, serialNumber, updateDS5Status, resetDS5Status, currentMovementData, stationName, shouldCheckStatus]);

  useEffect(() => {
    if (ds5Status.isLoading) return;
    if (statusCheckCompleted) return;
    if (statusInitialLoadRef.current && !ds5Status.isLoading && ds5Status.status !== 'unknown') {
      statusInitialLoadRef.current = false;
      setStatusCheckCompleted(true);
    }
  }, [ds5Status.isLoading, ds5Status.status, statusCheckCompleted]);

  // ─── Auto-save for KO / unknown status ──────────────────────────────────────
  useEffect(() => {
    const performAutoSave = async () => {
      if (
        !currentMovementData ||
        currentMovementData.nextjob !== stationName ||
        hasBeenProcessed ||
        !userInfo?.id ||
        !serialNumber ||
        !movementsCache || Object.keys(movementsCache).length === 0
      ) return;
      if (!statusCheckCompleted) return;
      if (ds5Status.status === 'ok') return;
      if ((ds5Status.status === 'ko' || ds5Status.status === 'unknown') && !autoSaveCompleted) {
        const autoGrade = '';
        const nextJobTarget = getNextJobByStatusAndGrade(autoGrade);
        const movementId = movementsCache[stationName];
        const nextJobId = movementsCache[nextJobTarget];
        if (!movementId || !nextJobId) return;
        const payload = {
          serial_number: serialNumber,
          operator: userInfo.id,
          tester: stationName,
          movement: movementId,
          nextjob: nextJobId,
          Grade: autoGrade,
          version: APP_VERSION,
          color: ds5color,
          id_palette: null
        };
        try {
          setIsSaving(true);
          setDs5StatusAtSave(ds5Status.status);
          await api.saveMovement(payload);
          setNextJob(nextJobTarget);
          setSelectedGrade('');
          setShowSuccess(true);
          setHasBeenProcessed(true);
          setAutoSaveCompleted(true);
          setShowPaletteWarning(false);
          // ✅ Palette stays selected
        } catch (err) {
          console.error("Erreur auto-save:", err);
          setWarning("Erreur lors de l'enregistrement automatique");
        } finally {
          setIsSaving(false);
        }
      }
    };
    performAutoSave();
  }, [
    currentMovementData, ds5Status, statusCheckCompleted, userInfo,
    hasBeenProcessed, autoSaveCompleted, isSaving, stationName,
    movementsCache, serialNumber, ds5color
  ]);

  // ─── Main save function ──────────────────────────────────────────────────────
  const saveToDatabase = async (grade) => {
    if (!userInfo || !userInfo.id) {
      setWarning("Aucun opérateur connecté. Veuillez vous authentifier.");
      return;
    }
    if (!currentMovementData || !movementsCache || Object.keys(movementsCache).length === 0) {
      setWarning("Données manquantes pour l'enregistrement");
      return;
    }
    if (!selectedPaletteId) {
      setWarning("Veuillez sélectionner une palette avant de valider.");
      return;
    }
    const currentPalette = palettes.find(p => p.id === Number(selectedPaletteId));
    if (!currentPalette) {
      setWarning("Palette introuvable. Veuillez en sélectionner une valide.");
      return;
    }
    if (showPaletteWarning) {
      setWarning("Impossible d'enregistrer: " + paletteWarningMessage);
      return;
    }
    if (currentPalette.qty >= currentPalette.capacity) {
      setWarning("Cette palette est pleine. Une nouvelle palette va être créée.");
      const colorCategory = getColorCategory(ds5color);
      createNewPalette(grade, colorCategory);
      return;
    }

    const nextJobTarget = getNextJobByStatusAndGrade(grade);
    const movementId = movementsCache[stationName];
    const nextJobId = movementsCache[nextJobTarget];
    if (!movementId || !nextJobId) {
      setWarning("Erreur: IDs des mouvements non trouvés");
      return;
    }

    const payload = {
      serial_number: serialNumber,
      operator: userInfo.id,
      tester: stationName,
      movement: movementId,
      nextjob: nextJobId,
      Grade: grade,
      version: APP_VERSION,
      color: ds5color,
      id_palette: Number(selectedPaletteId)
    };

    try {
      setIsSaving(true);
      setDs5StatusAtSave(ds5Status.status);
      await api.saveMovement(payload);
      await loadPalettes();
      setHourCount(prev => prev + 1);
      setNextJob(nextJobTarget);
      setSelectedGrade(grade);
      setShowSuccess(true);
      setHasBeenProcessed(true);
      setShowPaletteWarning(false);
      // ✅ Palette stays selected
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      setWarning(`Erreur enregistrement: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Reset on serial number change — palette always stays ────────────────────
  useEffect(() => {
    setShowSuccess(false);
    setNextJob('');
    setCurrentMovementData(null);
    setWarning('');
    setHasBeenProcessed(false);
    setSelectedGrade('');
    setAutoSaveCompleted(false);
    setDs5StatusAtSave('');
    setShouldCheckStatus(false);
    setStatusCheckCompleted(false);
    setShowPaletteWarning(false);
    setShowPaletteConfirmation(false);
    setPendingPaletteInfo(null);
    setShowPaletteSwitchConfirmation(false);
    setPaletteSwitchInfo(null);
    // ✅ selectedPaletteId and selectedPalette intentionally NOT reset here
    hasStatusBeenCheckedRef.current = false;
    statusInitialLoadRef.current = true;
  }, [serialNumber]);

  // ─── Render DS5 status badge ──────────────────────────────────────────────────
  const renderDS5Status = () => {
    if (ds5Status.isLoading) {
      return (
        <div className="ds5-status loading">
          <div className="status-text">Checking Full Test status...</div>
        </div>
      );
    }
    const statusConfig = {
      ok: { text: 'PASS', color: '#198754', className: 'pass', labelColor: '#495057' },
      ko: { text: 'FAIL', color: '#dc3545', className: 'fail', labelColor: '#495057' },
      error: { text: 'Connection Error', color: '#6c757d', className: 'error', labelColor: '#495057' },
      unknown: { text: 'Not Tested', color: '#ffc107', className: 'unknown', labelColor: '#495057' }
    };
    const config = statusConfig[ds5Status.status] || statusConfig.unknown;
    return (
      <div className={`ds5-status ${config.className}`}>
        <div className="status-text">
          <span style={{ color: config.labelColor }}>Status Full Test : </span>
          <span style={{ color: config.color, fontWeight: 'bold' }}>{config.text}</span>
        </div>
        {ds5Status.date && ds5Status.date !== 'unknown' && (
          <div className="status-date">Test Date: {ds5Status.date}</div>
        )}
        {ds5Status.message && (
          <div className="status-message">{ds5Status.message}</div>
        )}
      </div>
    );
  };

  const shouldShowButtons = () => {
    if (!currentMovementData || currentMovementData.nextjob !== stationName) return false;
    if (hasBeenProcessed || showSuccess || isSaving || autoSaveCompleted) return false;
    if (!statusCheckCompleted) return false;
    if (ds5Status.isLoading) return false;
    return ds5Status.status === 'ok';
  };

  const isAutoSaveInProgress = () => {
    if (!currentMovementData || currentMovementData.nextjob !== stationName) return false;
    return !ds5Status.isLoading &&
      ds5Status.status &&
      (ds5Status.status === 'ko' || ds5Status.status === 'unknown') &&
      !autoSaveCompleted &&
      !hasBeenProcessed &&
      isSaving;
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="Packaging-container">
      <div className="Packaging-card">
        <div className="Packaging-header">
          <h1>{stationName}</h1>
        </div>

        {!isConnected && (
          <div className="connection-section">
            <div className="disconnected">
              <h1>🔌 Plug in your DualSense controller.</h1>
            </div>
          </div>
        )}

        {isConnected && (
          <div className="connected-section">
            <div className="serial-display">
              <div className="serial-row">
                <label>Serial Number:</label>
                <div className="serial-value">{serialNumber}</div>
              </div>
              <div className="serial-row">
                <label>Color:</label>
                <div className="color-value">{ds5color}</div>
              </div>
            </div>

            {currentMovementData && currentMovementData.nextjob !== stationName && (
              <div className="wrong-station-section">
                <div className="message destination-message" style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  border: '2px solid #ffc107',
                  padding: '20px',
                  margin: '20px 0',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.1em', marginBottom: '5px' }}>
                    <strong>Next Job:</strong> <span className="next-job">{currentMovementData.nextjob}</span>
                  </div>
                </div>
              </div>
            )}

            {currentMovementData && currentMovementData.nextjob === stationName && (
              <div className="ds5-status-section">
                {renderDS5Status()}
              </div>
            )}

            {warning && (
              <div className="message warning-message">{warning}</div>
            )}

            {isSaving && (
              <div className="message waiting-message">
                {isAutoSaveInProgress() ? "Enregistrement automatique en cours..." : "Enregistrement en cours..."}
              </div>
            )}

            {/* Palette Creation Confirmation */}
            {showPaletteConfirmation && pendingPaletteInfo && (
              <div className="palette-popup-overlay">
                <div className="palette-confirmation-dialog">
                  <div className="confirmation-header">
                    <h3>⚠️ Create New Palette?</h3>
                  </div>
                  <div className="confirmation-details">
                    <p><strong>Grade:</strong> {pendingPaletteInfo.grade}</p>
                    <p><strong>Couleur:</strong> {pendingPaletteInfo.colorCategory}</p>
                    <p className="confirmation-note">No compatible palette found. Do you want to create a new palette?</p>
                  </div>
                  <div className="confirmation-buttons">
                    <button onClick={confirmPaletteCreation} className="btn-confirm-yes" disabled={isCreatingPalette}>YES</button>
                    <button onClick={cancelPaletteCreation} className="btn-confirm-no" disabled={isCreatingPalette}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Palette Switch Confirmation */}
            {showPaletteSwitchConfirmation && paletteSwitchInfo && (
              <div className="palette-popup-overlay">
                <div className="palette-confirmation-dialog">
                  <div className="confirmation-header">
                    <h3>⚠️ Switch Palette?</h3>
                  </div>
                  <div className="confirmation-details">
                    <p className="confirmation-note">The currently selected palette is not compatible with this grade and color.</p>
                    <div className="palette-switch-details">
                      <div className="old-palette">
                        <strong>Current Palette:</strong>
                        <div className="palette-info">
                          <div>{paletteSwitchInfo.oldPalette?.sn_palette}</div>
                          <div>
                            <span className={`grade-badge grade-${paletteSwitchInfo.oldPalette?.grad?.toLowerCase()}`}>{paletteSwitchInfo.oldPalette?.grad}</span>
                            {' '}
                            <span className="color-badge">{paletteSwitchInfo.oldPalette?.color}</span>
                          </div>
                        </div>
                      </div>
                      <div className="arrow">→</div>
                      <div className="new-palette">
                        <strong>New Compatible Palette:</strong>
                        <div className="palette-info">
                          <div>{paletteSwitchInfo.newPalette?.sn_palette}</div>
                          <div>
                            <span className={`grade-badge grade-${paletteSwitchInfo.newPalette?.grad?.toLowerCase()}`}>{paletteSwitchInfo.newPalette?.grad}</span>
                            {' '}
                            <span className="color-badge">{paletteSwitchInfo.newPalette?.color}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="confirmation-note">Do you want to switch to the compatible palette?</p>
                  </div>
                  <div className="confirmation-buttons">
                    <button onClick={confirmPaletteSwitch} className="btn-confirm-yes">Yes</button>
                    <button onClick={cancelPaletteSwitch} className="btn-confirm-no">Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {isCreatingPalette && (
              <div className="message waiting-message">Création automatique de la palette...</div>
            )}

            {showSuccess && nextJob && (
              <div
                className={`message success-message permanent ${ds5StatusAtSave === 'ko' && nextJob === "Repair" ? 'fail' : 'pass'}`}
                style={{
                  backgroundColor: ds5StatusAtSave === 'ko' && nextJob === "Repair" ? '#f8d7da' : '#d4edda',
                  color: ds5StatusAtSave === 'ko' && nextJob === "Repair" ? '#721c24' : '#155724',
                  border: `2px solid ${ds5StatusAtSave === 'ko' && nextJob === "Repair" ? '#dc3545' : '#28a745'}`
                }}
              >
                <div className="success-details">
                  <div><strong>Next Job:</strong> <span className="next-job">{nextJob}</span></div>
                  {selectedGrade && <div><strong>Grade:</strong> <span className="grade-value">{selectedGrade}</span></div>}
                </div>
              </div>
            )}

            {shouldShowButtons() && (
              <div className="buttons-section">
                <div className="grade-buttons-row">
                  {['A', 'B', 'C'].map((grade) => (
                    <button
                      key={grade}
                      onClick={() => { setWarning(''); setSelectedGrade(grade); }}
                      className={`btn-grade grade-${grade.toLowerCase()} ${selectedGrade === grade ? "selected" : ""}`}
                      disabled={isCreatingPalette || showPaletteConfirmation || showPaletteSwitchConfirmation}
                    >
                      {grade}
                    </button>
                  ))}
                </div>

                {showPaletteWarning && (
                  <div className="palette-warning-box">{paletteWarningMessage}</div>
                )}

                {selectedGrade && !selectedPaletteId && !isCreatingPalette && !showPaletteConfirmation && !showPaletteSwitchConfirmation && (
                  <div className="palette-warning-box">
                    ⚠️ No palette selected. Please select or create a palette before validating.
                  </div>
                )}

                <div className="action-buttons-row">
                  <button
                    onClick={() => {
                      if (!canValidate) {
                        if (!selectedGrade) setWarning("Veuillez sélectionner un grade.");
                        else if (!selectedPaletteId) setWarning("Veuillez sélectionner une palette avant de valider.");
                        return;
                      }
                      saveToDatabase(selectedGrade);
                    }}
                    disabled={!canValidate}
                    className={`btn-validate-grade ${canValidate ? 'btn-validate-ready' : 'btn-validate-disabled'}`}
                    title={!selectedGrade ? "Select a grade first" : !selectedPaletteId ? "No palette selected" : showPaletteWarning ? "Palette mismatch" : ""}
                  >
                    {isSaving ? 'Enregistrement...' : 'VALIDATE GRADE'}
                  </button>

                  <button
                    onClick={() => {
                      setShowFailModal(true);
                      setSelectedPanneId('');
                      setSelectedPanneInfo(null);
                      setSelectedMovementId('');
                      setModalError('');
                    }}
                    disabled={isSaving || isCreatingPalette || showPaletteConfirmation || showPaletteSwitchConfirmation || isFailSaving}
                    className="btn-fail"
                  >
                    {isFailSaving ? 'Enregistrement...' : 'FAIL'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Side - Cadance + Palette Selector */}
      <Cadance userInfo={userInfo} movementId={6} hourCount={hourcount} />
      <div className="cadence-palette-wrapper">
        <div className="palette-section">
          <div className="palette-dropdown-wrapper">
            <label htmlFor="palette-select">Palette:</label>
            <select
              id="palette-select"
              value={selectedPaletteId}
              onChange={(e) => { setSelectedPaletteId(e.target.value); setWarning(''); }}
              disabled={isCreatingPalette || showPaletteConfirmation || showPaletteSwitchConfirmation}
              className="palette-select"
            >
              <option value="">-- select palette --</option>
              {palettes.map((palette) => {
                const quantity = palette.qty || 0;
                const capacity = palette.capacity || 0;
                const isFull = quantity >= capacity;
                return (
                  <option key={palette.id} value={palette.id} disabled={isFull}>
                    {palette.sn_palette} 
                  </option>
                );
              })}
            </select>

            <table className="palette-table">
              <thead>
                <tr>
                  <th>SN Palette</th>
                  <th>Grade</th>
                  <th>Color</th>
                  <th>Qty</th>
                  <th>Cap.</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedPaletteId && (() => {
                  const freshPalette = palettes.find(p => String(p.id) === String(selectedPaletteId));
                  if (!freshPalette) return null;
                  const quantity = freshPalette.qty || 0;
                  const capacity = freshPalette.capacity || 0;
                  return (
                    <tr className="selected-row">
                      <td>{freshPalette.sn_palette}</td>
                      <td><span className={`grade-badge grade-${freshPalette.grad.toLowerCase()}`}>{freshPalette.grad}</span></td>
                      <td>{freshPalette.color}</td>
                      <td style={{ color: quantity >= capacity ? '#dc3545' : 'inherit', fontWeight: quantity >= capacity ? 'bold' : 'normal' }}>{quantity}</td>
                      <td>{capacity}</td>
                      <td><span className={`status-badge status-${freshPalette.id_mvtpal}`}>{freshPalette.status_name}</span></td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Fail Modal */}
      {showFailModal && (
        <div className="fail-modal-overlay" onClick={() => { setShowFailModal(false); setModalError(''); setSelectedPanneId(''); setSelectedPanneInfo(null); setSelectedMovementId(''); }}>
          <div className="fail-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Déclaration de Non-Conformité</h2>
            {modalError && <div className="message error-message modal-error">{modalError}</div>}
            <div className="modal-field">
              <label>Sélectionnez le type de panne:</label>
              <select value={selectedPanneId} onChange={e => { setSelectedPanneId(e.target.value); handlePanneSelection(e.target.value); setModalError(''); }}>
                <option value="">-- Choisir une panne --</option>
                {pannes.map(p => <option key={p.id} value={p.id}>{p.panne || p.name}</option>)}
              </select>
            </div>
            {selectedPanneInfo && (
              <div className="panne-info-display">
                <div className="info-item"><strong>Panne sélectionnée:</strong> {selectedPanneInfo.panne || selectedPanneInfo.name}</div>
              </div>
            )}
            <div className="modal-buttons">
              <button onClick={prepareConfirmation} className="btn-confirm">Confirmer</button>
              <button onClick={() => setShowFailModal(false)} className="btn-cancel">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && confirmationData && (
        <div className="fail-modal-overlay" onClick={() => setShowConfirmationModal(false)}>
          <div className="fail-modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <h2>Confirmation</h2>
            <div className="confirmation-details">
              <div className="confirmation-item"><strong>Panne:</strong> {confirmationData.panneText}</div>
              <div className="confirmation-item"><strong>Next Job:</strong> {confirmationData.target}</div>
            </div>
            <div className="confirmation-warning">Vérifiez bien ces informations avant de confirmer !</div>
            <div className="modal-buttons">
              <button onClick={handleFailConfirm} disabled={isFailSaving} className="btn-confirm">
                {isFailSaving ? 'Enregistrement...' : 'Confirmer'}
              </button>
              <button onClick={() => setShowConfirmationModal(false)} className="btn-cancel" disabled={isFailSaving}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackagingStation;